
import React, { useEffect, useMemo, useState } from 'react';
import './css/BenefitReportsPanel.css';

const API_BASE = 'https://ivevzjrqzs3z3fasduidlmeyqq0cntfs.lambda-url.us-east-1.on.aws';
const BENEFITS = [
  {
    id: 'insights',
    title: 'AI Financial Insights & Repayment Watch',
    titleEs: 'Análisis con IA y seguimiento de pagos',
    text: 'Easy-to-read account insights, including verified repayment activity and overdue-payment warnings for lenders when due dates are available.',
    textEs: 'Análisis claros de tu cuenta y avisos para prestamistas sobre pagos vencidos, solo cuando las fechas estén verificadas.'
  },
  {
    id: 'digest',
    title: 'Daily Activity Digest',
    titleEs: 'Resumen diario de actividad',
    text: 'Get a concise summary of recent account activity and important changes.',
    textEs: 'Recibe un resumen conciso de la actividad reciente de tu cuenta y de los cambios importantes.'
  },
  {
    id: 'monthly',
    title: 'Monthly Financial Report',
    titleEs: 'Informe financiero mensual',
    text: 'See monthly activity, charts, and verified Base fee reimbursements, with a downloadable PDF.',
    textEs: 'Consulta tu actividad, gráficos y reembolsos verificados de Base, con PDF descargable.'
  },
  {
    id: 'base-fees',
    title: 'Deposit Fee Reimbursements',
    titleEs: 'Reembolso de comisiones de depósito',
    text: 'Up to two eligible completed deposits per UTC month, with up to 5 USD total verified customer-paid fees and conversion losses. Excludes Lenderblock-funded costs.',
    textEs: 'Hasta dos depósitos completados elegibles por mes UTC y un máximo total de 5 USD en costos verificados pagados por ti. No incluye costos cubiertos por Lenderblock.'
  },
  {
    id: 'radar',
    title: 'Opportunity Radar',
    titleEs: 'Radar de oportunidades',
    text: 'Find credit lines with recent verified borrower repayments, withdrawals, or staking commission activity.',
    textEs: 'Descubre líneas de crédito con pagos, retiros o comisiones de staking verificados recientemente.'
  },
  {
    id: 'tax',
    title: 'Annual Tax Activity Report',
    titleEs: 'Informe anual para impuestos',
    text: 'A year-end summary of verified transactions, earnings, principal movements, and fees for your records. Full-year coverage begins in 2027.',
    textEs: 'Resumen de fin de año de transacciones, ganancias, movimientos de capital y comisiones verificadas. La cobertura anual completa empieza en 2027.'
  },
  {
    id: 'support',
    title: 'Priority Support',
    titleEs: 'Atención prioritaria',
    text: 'Reach Blender Pro Plus support with a faster, member-priority experience.',
    textEs: 'Contacta a Blender Pro Plus Support con una experiencia más rápida y prioritaria para miembros.'
  }
];

const LABELS = {
  transactions: ['Total Transactions', 'Total de transacciones', 'count'],
  depositsUSD: ['External Deposits', 'Depósitos externos', 'usd'],
  withdrawalsUSD: ['External Withdrawals', 'Retiros externos', 'usd'],
  repaymentsUSD: ['Repayments Made', 'Pagos realizados', 'usd'],
  lendingContributionsUSD: ['Lending Contributions', 'Aportes de préstamo', 'usd'],
  stakesUSD: ['Staking Contributions', 'Aportes de staking', 'usd'],
  rewardClaimsUSD: ['Reward Claims', 'Reclamos de recompensas', 'usd'],
  reclaimsUSD: ['Reclaims', 'Recuperaciones', 'usd'],
  repaymentsReceivedUSD: ['Repayments Received', 'Pagos recibidos', 'usd'],
  creditDrawsUSD: ['Borrowing / Credit Draws', 'Préstamos / Retiros de crédito', 'usd'],
  transfersInUSD: ['Transfers In', 'Transferencias entrantes', 'usd'],
  transfersOutUSD: ['Transfers Out', 'Transferencias salientes', 'usd'],
  stakerCollateralXDAI: ['Staker Collateral', 'Colateral en staking', 'xdai'],
  stakerRewardsClaimedXDAI: ['Rewards Claimed', 'Recompensas reclamadas', 'xdai'],
  stakerPaidOutXDAI: ['Rewards Paid Out', 'Recompensas pagadas', 'xdai'],
  stakerPendingRewardsXDAI: ['Pending Rewards', 'Recompensas pendientes', 'xdai'],
  lenderLiquidXDAI: ['Lender Liquid', 'Liquidez del prestamista', 'xdai'],
  lenderFinalizedXDAI: ['Lender Finalized', 'Préstamos finalizados', 'xdai'],
  lenderRepaymentsReceivedXDAI: ['Lender Repayments Received', 'Pagos al prestamista', 'xdai'],
  interestReceivedRecordedXDAI: ['Recorded Interest Received', 'Interés registrado recibido', 'xdai']
};

// All activity types appear in a fixed order, even when an individual category is zero.
// If the API omits a category, show a dash (unavailable), not a fabricated zero.
const ACTIVITY_GROUPS = [
  { title: 'Money Movement', titleEs: 'Movimiento de fondos', keys: ['depositsUSD','withdrawalsUSD','transfersInUSD','transfersOutUSD'] },
  { title: 'Lending & Borrowing', titleEs: 'Préstamos y crédito', keys: ['lendingContributionsUSD','creditDrawsUSD','repaymentsUSD','repaymentsReceivedUSD','reclaimsUSD'] },
  { title: 'Staking & Rewards', titleEs: 'Staking y recompensas', keys: ['stakesUSD','rewardClaimsUSD'] }
];

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatNumber(value, digits = 2) {
  const num = toNumber(value);
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: num % 1 === 0 ? 0 : Math.min(2, digits), maximumFractionDigits: digits }).format(num);
}

function getLabel(key, spanish) {
  const entry = LABELS[key];
  if (entry) return spanish ? entry[1] : entry[0];
  return key;
}

function getUnit(key) {
  const entry = LABELS[key];
  return entry ? entry[2] : 'text';
}

function formatValue(key, value) {
  const unit = getUnit(key);
  if (unit === 'count') return formatNumber(value, 0);
  if (unit === 'usd') return `${formatNumber(value)} USD`;
  if (unit === 'xdai') return `${formatNumber(value, 4)} xDAI`;
  return String(value ?? '');
}

function buildMonths() {
  const start = new Date(Date.UTC(2026, 9, 1));
  const now = new Date();
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const months = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    months.push(cursor.toISOString().slice(0, 7));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return months.reverse();
}

function escapeHtml(value) {
  return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function normalizeActivityTotals(totals) {
  const result = { ...(totals || {}) };
  // Rescind and reclaim are two names for the same contract action.
  // Prefer the canonical recorded reclaim amount rather than adding possibly duplicated totals.
  if (result.reclaimsUSD == null && result.rescindsUSD != null) result.reclaimsUSD = result.rescindsUSD;
  delete result.rescindsUSD;
  return result;
}

function exportReportToPdf({ month, spanish, reportTotals, memberMetrics, insightText, verifiedFeeReimbursement, memberProfile }) {
  const profile = memberProfile || {};
  const safeField = value => escapeHtml(value && String(value).trim() ? value : (spanish ? 'No disponible' : 'Not available'));
  const profileRows = [
    [spanish ? 'Nombre completo' : 'Full name', profile.fullName],
    [spanish ? 'Nombre de usuario' : 'Username', profile.username],
    [spanish ? 'Dirección' : 'Address', profile.address],
    [spanish ? 'Miembro desde' : 'Date joined', profile.dateJoined]
  ].map(([label, value]) => `<tr><td>${label}</td><td style="text-align:right">${safeField(value)}</td></tr>`).join('');
  const reportRows = Object.entries(reportTotals || {}).map(([key, value]) => `
    <tr><td>${escapeHtml(getLabel(key, spanish))}</td><td style="text-align:right">${escapeHtml(formatValue(key, value))}</td></tr>
  `).join('');
  const memberRows = Object.entries(memberMetrics || {}).map(([key, value]) => `
    <tr><td>${escapeHtml(getLabel(key, spanish))}</td><td style="text-align:right">${escapeHtml(formatValue(key, value))}</td></tr>
  `).join('');
  const html = `<!doctype html><html><head><meta charset="utf-8" /><title>Blender Pro Plus ${month}</title>
  <style>
    body{font-family:Arial,sans-serif;padding:32px;color:#18352d}
    h1{margin:0 0 8px;font-size:28px}
    h2{margin:28px 0 12px;font-size:18px}
    p{line-height:1.55}
    table{width:100%;border-collapse:collapse;margin-top:10px}
    td,th{padding:10px 12px;border-bottom:1px solid #dde7e0;font-size:14px}
    .muted{color:#51675d;font-size:12px}
    .card{border:1px solid #d7e2dc;border-radius:14px;padding:18px;margin-top:18px}
  </style></head><body>
  <h1>Blender Pro Plus</h1>
  <p>${spanish ? 'Informe exportado para' : 'Report exported for'} ${escapeHtml(month)}</p>
  <div class="card"><h2>${spanish ? 'Información del miembro' : 'Member information'}</h2><table>${profileRows}</table></div>
  <div class="card"><h2>${spanish ? 'Actividad mensual' : 'Monthly Activity Snapshot'}</h2><table>${reportRows}</table></div>
  ${memberRows ? `<div class="card"><h2>${spanish ? 'Posiciones verificadas actuales' : 'Verified Current Positions'}</h2><table>${memberRows}</table></div>` : ''}
  ${verifiedFeeReimbursement !== null && Number.isFinite(Number(verifiedFeeReimbursement)) ? `<div class="card"><h2>${spanish ? 'Reembolsos verificados de Base' : 'Verified Base fee reimbursements'}</h2><p>${formatNumber(verifiedFeeReimbursement)} USD</p><p class="muted">${spanish ? 'Importe reembolsado, no todas las comisiones pagadas.' : 'Amount actually reimbursed, not total fees incurred.'}</p></div>` : ''}
  ${insightText ? `<div class="card"><h2>${spanish ? 'Resumen con IA' : 'AI summary'}</h2><p>${escapeHtml(insightText)}</p></div>` : ''}
  <div class="card"><h2>${spanish ? 'Resumen para tus registros fiscales' : 'Summary for your tax records'}</h2>
  <p>${spanish ? 'Este reporte separa depósitos, retiros, préstamos, staking, pagos y recompensas para facilitar el registro. Los importes no representan por sí solos ganancias imponibles ni deducciones.' : 'This report separates deposits, withdrawals, lending, staking, repayments, and rewards to help organize your records. These totals do not by themselves establish taxable income, gains, or deductions.'}</p>
  <p>${spanish ? 'Guarda también recibos, fechas, comisiones, historial de costos y transacciones, y documentos fiscales de los proveedores. Solo incluye actividad disponible a partir de octubre de 2026.' : 'Keep your transaction dates, receipts, fees, cost-basis records, and any provider tax forms as well. This report only covers available activity from October 2026 onward.'}</p></div>
  <p class="muted">${spanish ? 'Exportado desde Blender Pro Plus. Usa “Guardar como PDF” en el cuadro de impresión de tu navegador.' : 'Exported from Blender Pro Plus. Use “Save as PDF” in your browser print dialog.'}</p>
  <script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
  </body></html>`;
  const popup = window.open('', '_blank', 'width=960,height=720');
  if (!popup) return;
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
}

function friendlyMonthSummary(totals, positions, spanish) {
  const count = Number(totals?.transactions ?? 0);
  const deposits = Number(totals?.depositsUSD ?? 0);
  const stakes = Number(totals?.stakesUSD ?? 0);
  const rewards = Number(totals?.rewardClaimsUSD ?? 0);
  const collateral = Number(positions?.stakerCollateralXDAI ?? 0);
  const fmt = value => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  if (spanish) return [
    {heading:'Este mes',text:`Registraste ${fmt(count)} transacciones. Depositaste ${fmt(deposits)} USD y aportaste ${fmt(stakes)} USD a staking.`},
    {heading:'Tu dinero en la plataforma',text: collateral > 0 ? `Tus contratos muestran ${fmt(collateral)} xDAI en colateral de staking.` : 'Puedes revisar tus aportes y movimientos en el resumen de arriba.'},
    {heading:'Recompensas',text: rewards > 0 ? `Registraste ${fmt(rewards)} USD en reclamos de recompensas este mes.` : 'No aparecen reclamos de recompensas en este resumen mensual.'},
    {heading:'Para revisar',text:'Consulta tus contratos para conocer sus próximos pagos y las recompensas disponibles.'}
  ];
  return [
    {heading:'This month',text:`You made ${fmt(count)} transactions. You deposited ${fmt(deposits)} USD and put ${fmt(stakes)} USD into staking.`},
    {heading:'Your money at work',text:collateral > 0 ? `Your contracts currently show ${fmt(collateral)} xDAI in staking collateral.` : 'You can review your deposits and staking activity in the snapshot above.'},
    {heading:'Rewards',text:rewards > 0 ? `You claimed ${fmt(rewards)} USD in rewards this month.` : 'No reward claims appear in this monthly summary.'},
    {heading:'Next steps',text:'Open your contracts to review payment details and available rewards.'}
  ];
}

export default function BenefitReportsPanel({ getMembershipIdentity, active, spanish = false, platformBenefits = [], memberProfile = {} }) {
  // Approved member benefits; no legacy fee-free lending or deposit promises.
  const allBenefits = BENEFITS;
  const [month, setMonth] = useState('2026-10');
  const [data, setData] = useState(null);
  const [reimbursements, setReimbursements] = useState(null);
  const [reimbursementError, setReimbursementError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const months = useMemo(() => buildMonths(), []);

  useEffect(() => {
    if (!active) { setData(null); setReimbursements(null); setLoading(false); setError(''); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const { accessToken } = await getMembershipIdentity();
        const response = await fetch(`${API_BASE}/benefits/reports?month=${encodeURIComponent(month)}`, {
          headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
          cache: 'no-store'
        });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.error || 'Unable to load reports');
        if (!cancelled) setData(result);
        try {
          const benefitResponse = await fetch(`${API_BASE}/benefits/reimbursements?month=${encodeURIComponent(month)}`, {
            headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' }, cache: 'no-store'
          });
          const benefitResult = await benefitResponse.json();
          if (!benefitResponse.ok || benefitResult.success !== true) throw new Error(benefitResult.error || 'Unavailable');
          if (!cancelled) {setReimbursements(benefitResult);setReimbursementError('');}
        } catch (reimbursementFailure) {
          if (!cancelled) {setReimbursements(null);setReimbursementError('Reimbursement history unavailable');}
        }
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setError(err.message || 'Reports unavailable');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active, month, getMembershipIdentity]);

  const reportTotals = normalizeActivityTotals(data?.report?.totals || data?.reportTotals || data?.receiptMetrics || {});
  const memberMetrics = data?.aiInsight?.memberMetricsXDAI || data?.memberMetricsXDAI || data?.report?.memberMetricsXDAI || {};
  const insightText = data?.aiInsight?.insight || data?.insight || '';
  const verifiedFeeReimbursement = data?.report?.verifiedBaseFeeReimbursementsUSD ?? data?.verifiedBaseFeeReimbursementsUSD ?? null;
  const chartMax = Math.max(1, ...ACTIVITY_GROUPS.flatMap(group => group.keys)
    .map(key => Object.prototype.hasOwnProperty.call(reportTotals, key) ? Math.max(0, toNumber(reportTotals[key])) : 0));
  const memberEntries = Object.entries(memberMetrics).filter(([, value]) => toNumber(value) > 0);
  const emailSettingsText = data?.settings?.emailOptIn
    ? (spanish ? 'Activadas' : 'Enabled')
    : (spanish ? 'Desactivadas' : 'Disabled');

  return (
    <section className="tbp-plus-panel" aria-label="Blender Pro Plus overview">
      <div className="tbp-plus-panel__hero">
        <div>
          <div className="tbp-plus-panel__eyebrow">BLENDER PRO PLUS</div>
          <h2>{spanish ? 'Tu centro de beneficios premium' : 'Your Premium Benefits Hub'}</h2>
          <p>{spanish
            ? 'Consulta beneficios, informes guardados, gráficos de actividad y análisis con IA desde un mismo lugar.'
            : 'Review benefits, saved reports, activity charts, and AI analysis from one place.'}
          </p>
        </div>
        <div className="tbp-plus-panel__pill">{active ? (spanish ? 'Membresía activa' : 'Active membership') : (spanish ? 'Disponible con Pro Plus' : 'Available with Pro Plus')}</div>
      </div>

      <div className="tbp-plus-panel__benefits">
        {allBenefits.map((item, index) => (
          <article className="tbp-plus-panel__benefit" key={item.id}>
            <span className="tbp-plus-panel__benefit-index">0{index + 1}</span>
            <strong>{spanish ? item.titleEs : item.title}</strong>
            {(spanish ? item.textEs : item.text) ? <p>{spanish ? item.textEs : item.text}</p> : null}
          </article>
        ))}
      </div>

      {active ? (
        <div className="tbp-plus-panel__report-shell">
          <div className="tbp-plus-panel__toolbar">
            <div>
              <h3>{spanish ? 'Informe guardado del miembro' : 'Saved Member Report'}</h3>
              <p>{spanish ? 'Tus totales mensuales y tus posiciones verificadas actuales.' : 'Your monthly totals and verified current positions.'}</p>
            </div>
            <div className="tbp-plus-panel__toolbar-actions">
              <label>
                <span>{spanish ? 'Mes' : 'Month'}</span>
                <select value={month} onChange={(event) => setMonth(event.target.value)}>
                  {months.map((monthOption) => <option value={monthOption} key={monthOption}>{monthOption}</option>)}
                </select>
              </label>
              <button
                type="button"
                className="tbp-plus-panel__export"
                onClick={() => exportReportToPdf({ month, spanish, reportTotals, memberMetrics, verifiedFeeReimbursement, memberProfile, insightText: Object.keys(reportTotals).length ? friendlyMonthSummary(reportTotals, memberMetrics, spanish).map(part => `${part.heading}: ${part.text}`).join('\n\n') : insightText })}
                disabled={!Object.keys(reportTotals).length && !insightText}
              >
                {spanish ? 'Exportar PDF' : 'Export PDF'}
              </button>
            </div>
          </div>

          <section className="tbp-plus-panel__reimbursements" aria-label="Deposit reimbursements">
            <h3>{spanish ? 'Reembolsos de tus depósitos' : 'Your Deposit Reimbursements'}</h3>
            {reimbursementError ? <p role="status">{spanish ? 'Historial de reembolsos no disponible.' : reimbursementError}</p> : null}
            {reimbursements ? <>
              <div className="tbp-plus-panel__reimbursement-stats">
                <div><span>{spanish ? 'Reembolsado' : 'Paid to you'}</span><strong>{Number(reimbursements.allowance?.reimbursedUSD || 0).toFixed(2)} USD</strong></div>
                <div><span>{spanish ? 'Depósitos contabilizados' : 'Deposits counted'}</span><strong>{reimbursements.allowance?.countedDeposits || 0} / 2</strong></div>
                <div><span>{spanish ? 'Reservado del límite' : 'Reserved against limit'}</span><strong>{Number(reimbursements.allowance?.reservedUSD || 0).toFixed(2)} / 5.00 USD</strong></div>
                <div><span>{spanish ? 'Disponible' : 'Remaining allowance'}</span><strong>{Number(reimbursements.allowance?.remainingUSD || 0).toFixed(2)} USD</strong></div>
              </div>
              <div className="tbp-plus-panel__reimbursement-history">
                {(reimbursements.reimbursements || []).length ? reimbursements.reimbursements.map(item => <div key={item.normalizationId} className="tbp-plus-panel__reimbursement-row">
                  <div><strong>{Number(item.amountUSD).toFixed(2)} USD</strong><small>{item.normalizationId}</small></div>
                  <div><span>{item.status === 'settled' ? (spanish ? 'Pagado' : 'Paid') : (spanish ? 'Pendiente' : 'Pending')}</span>
                    {item.transactionHash ? <a href={`https://gnosisscan.io/tx/${encodeURIComponent(item.transactionHash)}`} target="_blank" rel="noopener noreferrer">GnosisScan</a> : null}
                  </div>
                </div>) : <p>{spanish ? 'No hay reembolsos para este mes.' : 'No reimbursements for this month.'}</p>}
              </div>
            </> : null}
          </section>

          {loading ? <p className="tbp-plus-panel__notice">{spanish ? 'Cargando informe...' : 'Loading report...'}</p> : null}
          {!loading && error ? <p className="tbp-plus-panel__notice tbp-plus-panel__notice--error" role="alert">{error}</p> : null}
          {!loading && !error && !Object.keys(reportTotals).length && !insightText ? <p className="tbp-plus-panel__notice">{spanish ? 'Todavía no hay un informe guardado para este mes.' : 'No saved report is available for this month yet.'}</p> : null}

          {!loading && !error && verifiedFeeReimbursement !== null && Number.isFinite(Number(verifiedFeeReimbursement)) ? (
            <div className="tbp-plus-panel__notice" style={{textAlign:'left'}}>
              <strong>{spanish ? 'Reembolsos de Base recibidos este mes' : 'Base fees reimbursed this month'}:</strong> {formatNumber(verifiedFeeReimbursement)} USD
            </div>
          ) : null}
          {!loading && !error && (Object.keys(reportTotals).length > 0 || insightText) ? (
            <>
              <div className="tbp-plus-panel__grid tbp-plus-panel__grid--top">
                <section className="tbp-plus-panel__card">
                  <div className="tbp-plus-panel__cardhead">
                    <h4>{spanish ? 'Resumen mensual de actividad' : 'Monthly Activity Snapshot'}</h4>
                    {/* <span>{spanish ? 'Valores del informe' : 'Reported values'}</span> */}
                  </div>
                  <div className="tbp-plus-panel__metric-grid">
                    {Object.entries(reportTotals).map(([key, value]) => (
                      <div className="tbp-plus-panel__metric" key={key}>
                        <span>{getLabel(key, spanish)}</span>
                        <strong>{formatValue(key, value)}</strong>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="tbp-plus-panel__card"> 
                  <div className="tbp-plus-panel__cardhead">
                    <h4>{spanish ? 'Gráfico de actividad mensual' : 'Monthly Activity Chart'}</h4>
                    {/* <span>{spanish ? 'Todas las categorías' : 'All activity categories'}</span> */}
                  </div>
                  <div className="tbp-plus-panel__bars tbp-plus-panel__bars--all">
                    {ACTIVITY_GROUPS.map(group => (
                      <div className="tbp-plus-panel__activity-group" key={group.title}>
                        <h5 className="tbp-plus-panel__group-title">{spanish ? group.titleEs : group.title}</h5>
                        {group.keys.map(key => {
                          const available = Object.prototype.hasOwnProperty.call(reportTotals, key) && reportTotals[key] != null;
                          const value = available ? Math.max(0, toNumber(reportTotals[key])) : 0;
                          return (
                            <div className="tbp-plus-panel__bar-row" key={key}>
                              <div className="tbp-plus-panel__bar-labels">
                                <span>{getLabel(key, spanish)}</span>
                                <strong>{available ? formatValue(key, value) : (spanish ? 'Sin datos' : 'Not reported')}</strong>
                              </div>
                              <div className="tbp-plus-panel__bar-track" aria-hidden="true">
                                <div className="tbp-plus-panel__bar-fill" style={{ width: available ? `${(value / chartMax) * 100}%` : '0%' }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              <div className="tbp-plus-panel__grid tbp-plus-panel__grid--bottom">
                <section className="tbp-plus-panel__card">
                  <div className="tbp-plus-panel__cardhead">
                    <h4>{spanish ? 'Posiciones verificadas actuales' : 'Verified Current Positions'}</h4>
                    <span>{spanish ? 'Valores actuales en xDAI' : 'Current positions in xDAI'}</span>
                  </div>
                  {memberEntries.length ? (
                    <div className="tbp-plus-panel__metric-grid">
                      {memberEntries.map(([key, value]) => (
                        <div className="tbp-plus-panel__metric" key={key}>
                          <span>{getLabel(key, spanish)}</span>
                          <strong>{formatValue(key, value)}</strong>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="tbp-plus-panel__muted">{spanish ? 'Este informe no incluye una instantánea adicional de posiciones verificadas.' : 'This report does not include an additional verified-position snapshot.'}</p>
                  )}
                </section>

                <section className="tbp-plus-panel__card">
                  <div className="tbp-plus-panel__cardhead">
                    <h4>{spanish ? 'Tu resumen financiero' : 'Your Account Outlook'}</h4>
                    {/* <span>{spanish ? 'Basado en los datos del informe' : 'Based on your report data'}</span> */}
                  </div>
                  {Object.keys(reportTotals).length ? (
                    <div className="tbp-plus-panel__plain-summary">
                      {friendlyMonthSummary(reportTotals, memberMetrics, spanish).map(section => (
                        <div className="tbp-plus-panel__summary-section" key={section.heading}>
                          <strong>{section.heading}</strong><p>{section.text}</p>
                        </div>
                      ))}
                    </div>
                  ) : (insightText ? <p className="tbp-plus-panel__insight">{insightText}</p> : <p className="tbp-plus-panel__muted">{spanish ? 'Todavía no hay un resumen guardado.' : 'No saved summary is available yet.'}</p>)}
                  <div className="tbp-plus-panel__settings">
                    <span>{spanish ? 'Para impuestos: guarda recibos e historial de transacciones. Los movimientos y saldos no son una declaración de ingresos imponibles.' : 'For tax records: keep receipts and transaction history. Activity and balances are not a calculation of taxable income.'}</span>
                    <span>{spanish ? 'Notificaciones por correo' : 'Email notifications'}: <strong>{emailSettingsText}</strong></span>
                    <span>{spanish ? 'Las preferencias serán editables cuando esté disponible la verificación segura del correo.' : 'Preferences will become editable after secure email verification is available.'}</span>
                  </div>
                </section>
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div className="tbp-plus-panel__inactive-note">
          <p>{spanish ? 'Suscríbete para desbloquear tus informes guardados, exportaciones en PDF y resúmenes con IA.' : 'Subscribe to unlock your saved reports, PDF exports, and AI-powered summaries.'}</p>
        </div>
      )}
    </section>
  );
}
